import { money } from "@/lib/money";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const weekLabel = (ws) => {
  if (!ws) return "—";
  const [y, m, d] = ws.split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, d));
  const end = new Date(start.getTime() + 6 * 86400000);
  return `${MONTHS[start.getUTCMonth()]} ${start.getUTCDate()} – ${MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}`;
};
const hrs = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const PAYRUN_TONE = { paid: "bg-emerald-100 text-emerald-800 border-emerald-200", approved: "bg-blue-100 text-blue-800 border-blue-200", draft: "bg-slate-100 text-slate-600 border-slate-200" };

// Week-by-week draw vs. commission breakdown. `weeks` = rows from /sales/me/dashboard or /sales-reps/{id}/summary.
export function CompWeeksTable({ weeks = [], testId = "comp-weeks" }) {
  const totals = weeks.reduce((t, w) => ({ hours: t.hours + (w.hours || 0), commission: t.commission + (w.commission || 0), pay: t.pay + (w.pay || 0),
                                            over: t.over + (w.applies === "commission" ? 1 : 0) }), { hours: 0, commission: 0, pay: 0, over: 0 });
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm min-w-[860px]">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
          <tr>
            <th className="text-left px-4 py-3">Week</th>
            <th className="text-right px-4 py-3">Approved hrs</th>
            <th className="text-right px-4 py-3">Commission</th>
            <th className="text-right px-4 py-3">Draw</th>
            <th className="text-right px-4 py-3">Pay</th>
            <th className="text-left px-4 py-3">Break-even</th>
            <th className="text-left px-4 py-3">Pay run</th>
          </tr>
        </thead>
        <tbody>
          {weeks.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No weeks to show.</td></tr>}
          {weeks.map((w, i) => {
            const over = w.applies === "commission";
            if (w.before_plan) return (
              <tr key={w.week_start} className="border-t border-slate-100 text-slate-400" data-testid={`comp-week-${w.week_start}`}>
                <td className="px-4 py-3 font-medium whitespace-nowrap">{weekLabel(w.week_start)}</td>
                <td className="px-4 py-3 text-right font-mono">{hrs(w.hours)}</td>
                <td className="px-4 py-3 text-xs italic" colSpan={5}>Before the sales plan started — no draw or commission.</td>
              </tr>
            );
            return (
              <tr key={w.week_start} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`comp-week-${w.week_start}`}>
                <td className="px-4 py-3 font-medium whitespace-nowrap">{weekLabel(w.week_start)}{i === 0 && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700">this week</span>}</td>
                <td className="px-4 py-3 text-right font-mono">{hrs(w.hours)}{w.pending_hours > 0 && <div className="text-[11px] text-amber-700 font-sans">+{hrs(w.pending_hours)} pending</div>}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-600" title={`${hrs(w.hours)} hrs × ${money(w.rate_per_hour)}/hr`}>{money(w.commission)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-600">{money(w.weekly_draw)}</td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${over ? "text-emerald-700" : "text-slate-900"}`} data-testid={`comp-pay-${w.week_start}`}>{money(w.pay)}</td>
                <td className="px-4 py-3">
                  {over
                    ? <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold border bg-emerald-100 text-emerald-800 border-emerald-200">Commission · {hrs(w.hours_over_break_even)} hrs over</span>
                    : <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold border bg-amber-100 text-amber-800 border-amber-200">Draw · {hrs(w.hours_to_break_even)} hrs short</span>}
                </td>
                <td className="px-4 py-3">
                  {w.payrun_status
                    ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${PAYRUN_TONE[w.payrun_status] || PAYRUN_TONE.draft}`}>{w.payrun_status === "paid" ? `Paid ${money(w.paid_amount)}` : w.payrun_status}</span>
                    : <span className="text-xs text-slate-400">{i === 0 ? "in progress" : "not run yet"}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
        {weeks.length > 0 && (
          <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
            <tr data-testid="comp-weeks-total">
              <td className="px-4 py-3">{weeks.length} weeks · {totals.over} over break-even</td>
              <td className="px-4 py-3 text-right font-mono">{hrs(totals.hours)}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-600">{money(totals.commission)}</td>
              <td className="px-4 py-3" />
              <td className="px-4 py-3 text-right font-mono">{money(totals.pay)}</td>
              <td className="px-4 py-3" colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
